/**
 * Full Image Hero
 * 
 * Full-width image with text overlay.
 * The default hero style.
 */

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { heroImageService } from "@/services/heroImageService";
import { queryKeys } from "@/lib/query-config";
import { useState, useEffect } from "react";
import { RichTextDisplay } from "@/components/ui/rich-text-display";

const HeroFullImage = () => {
  const { features, data } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;
  
  const heroImageEnabled = (data?.features as Record<string, unknown>)?.heroImage !== false;

  // Fetch active hero images
  const { data: heroImages, isLoading: heroImagesLoading } = useQuery({
    queryKey: queryKeys.heroImages.active(),
    queryFn: async () => {
      const result = await heroImageService.getActiveHeroImages();
      if (!result.success) return [];
      return result.data;
    },
    enabled: heroImageEnabled,
    staleTime: 0, // Always fetch fresh data to show latest hero image
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Use first hero image if available, otherwise fallback to default
  // Avoid showing fallback while loading to prevent flash of wrong image
  const currentHeroImage = heroImagesLoading
    ? null
    : heroImages && heroImages.length > 0
    ? heroImages[0].photo?.image_url
    : null;

  // Create a stable cache-bust key based on hero image data (not Date.now() which changes every render)
  const cacheBustKey = heroImages?.[0]?.id && heroImages?.[0]?.updated_at
    ? `${heroImages[0].id}_${heroImages[0].updated_at}`
    : '';

  // Add cache-busting with stable key (no Date.now() to avoid infinite re-renders)
  const imageUrlWithCacheBust = currentHeroImage && cacheBustKey
    ? `${currentHeroImage}${currentHeroImage.includes('?') ? '&' : '?'}v=${cacheBustKey}`
    : currentHeroImage;

  // Track image loading state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [lastLoadedKey, setLastLoadedKey] = useState<string>('');

  // Reset loading state when the cache key changes (meaning a new image was set)
  useEffect(() => {
    const currentKey = cacheBustKey || 'default';
    if (currentKey !== lastLoadedKey) {
      setImageLoaded(false);
    }
  }, [cacheBustKey, lastLoadedKey]);

  // Handle successful image load
  const handleImageLoad = () => {
    setImageLoaded(true);
    setLastLoadedKey(cacheBustKey || 'default');
  };

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
    small: 'min-h-[40vh]',
  };

  const alignmentClasses = {
    left: 'text-left lg:text-left items-start',
    center: 'text-center items-center',
    right: 'text-right lg:text-right items-end',
  };

  return (
    <section className={cn(
      "relative flex items-center justify-center overflow-hidden",
      heightClasses[heroConfig.height]
    )}>
      {/* Background */}
      <div className="absolute inset-0 bg-primary">
        {heroImageEnabled ? (
          <>
            {/* Only show image once loaded to prevent flash of old image */}
            {imageLoaded && imageUrlWithCacheBust && (
              <img
                src={imageUrlWithCacheBust}
                alt={heroImages && heroImages.length > 0 && heroImages[0].title
                  ? heroImages[0].title
                  : `${siteConfig.organizationName} community`}
                className="absolute inset-0 w-full h-full object-cover"
                key={`hero-${heroImages?.[0]?.id || 'default'}-${heroImages?.[0]?.updated_at || 'default'}`}
              />
            )}
            {/* Preload image off-screen to trigger onLoad before displaying */}
            {imageUrlWithCacheBust && (
              <img
                src={imageUrlWithCacheBust}
                alt=""
                className="absolute opacity-0 pointer-events-none"
                onLoad={handleImageLoad}
                onError={handleImageLoad}
                style={{ position: 'absolute', left: '-9999px' }}
              />
            )}
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/60 to-transparent pointer-events-none"
              style={{ opacity: heroConfig.overlayOpacity / 100 }}
            />
          </>
        ) : (
          <div className="w-full h-full bg-primary" />
        )}
      </div>

      {/* Content */}
      <div className={cn(
        "relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col",
        alignmentClasses[heroConfig.contentAlignment]
      )}>
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {hero.title}
            <span className="block text-heritage">
              {hero.highlightedText}
            </span>
          </h1>
          
          <div className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            <RichTextDisplay content={hero.subtitle || ''} prose={false} />
          </div>

          {/* CTA Buttons */}
          {heroConfig.showCTA && (
            <div className={cn(
              "flex flex-col sm:flex-row gap-4",
              heroConfig.contentAlignment === 'center' && "justify-center",
              heroConfig.contentAlignment === 'right' && "justify-end"
            )}>
              {features.membership.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/membership">
                    <Users className="w-5 h-5" />
                    Join Our Community
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
              
              {features.donation.enabled && (
                <Button variant="donate" size="lg" asChild className="group">
                  <Link to="/donate">
                    <Heart className="w-5 h-5" />
                    Support Our Mission
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
              
              {features.events.enabled && (
                <Button variant="culture" size="lg" asChild className="group">
                  <Link to="/events">
                    <Calendar className="w-5 h-5" />
                    Experience Events
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}

              {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/about-us">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
            </div>
          )}

          {/* Statistics */}
          {heroConfig.showStats && hero.showStats && hero.stats.length > 0 && (
            <div className={cn(
              "mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8",
              heroConfig.contentAlignment === 'center' && "text-center"
            )}>
              {hero.stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-heritage/10 backdrop-blur-sm border border-heritage/20 rounded-lg p-6"
                >
                  <div className="text-3xl font-bold text-heritage">{stat.value}</div>
                  <div className="text-white/90 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroFullImage;

